import moment from 'moment-timezone'

export const EVENT_REPORT_TYPES = ['SOS_ALERT', 'INCIDENT', 'CANCEL_PATROL', 'FAILURE_PATROL']

const DEFAULT_PAGE = 1
const DEFAULT_LIMIT = 50

const asPositiveInteger = (value, fallback) => {
    const number = Number(value)
    return Number.isInteger(number) && number > 0 ? number : fallback
}

const toIsoString = value => {
    if (!value) return ''
    if (typeof value === 'string') return value

    try {
        return value.toISOString()
    } catch {
        return String(value)
    }
}

const displayName = (value, fallback = '') => {
    if (value === null || value === undefined) return stringValue(fallback)
    if (typeof value !== 'object') return stringValue(fallback) || String(value)
    if (value.fullName) return String(value.fullName)

    const joinedName = [value.firstName, value.lastName].filter(Boolean).join(' ')
    return joinedName || String(value.name || value.fantasyName || value.socialName || value.description || value.username || fallback || '')
}

const uniqueAllowedTypes = types => {
    const values = Array.isArray(types) ? types : String(types || '').split(',')
    return values.reduce((result, type) => {
        const normalizedType = String(type || '').trim().toUpperCase()
        if (EVENT_REPORT_TYPES.includes(normalizedType) && !result.includes(normalizedType)) result.push(normalizedType)
        return result
    }, [])
}

const normalizedSource = source => {
    const isLegacyMissingSource = source === null || source === undefined || (typeof source === 'string' && source.trim() === '')
    if (isLegacyMissingSource) return 'SHIELDGO'

    return displayName(source).trim().toUpperCase()
}

const stringValue = value => (value === null || value === undefined ? '' : String(value))

export const formatEventReportDate = (value, timezone = 'America/Sao_Paulo') => {
    if (!value) return ''

    const date = moment(value)
    if (!date.isValid()) return ''

    const resolvedTimezone = typeof timezone === 'string' && moment.tz.zone(timezone) ? timezone : 'America/Sao_Paulo'
    return date.tz(resolvedTimezone).format('DD/MM/YYYY HH:mm:ss')
}

export const parseEventReportTypePreset = preset => {
    const types = uniqueAllowedTypes(preset)
    return types.length > 0 ? types : [...EVENT_REPORT_TYPES]
}

export const buildEventReportRequest = (filters = {}, pagination = {}) => {
    const request = {
        report: 'EVENTS',
        types: parseEventReportTypePreset(filters.types),
        excludeSources: ['ALERTPORT'],
    }

    ;['account', 'client', 'site', 'vigilant'].forEach(field => {
        if (filters[field]) request[field] = filters[field]
    })

    const startDate = toIsoString(filters.startDate)
    const endDate = toIsoString(filters.endDate)
    if (startDate) request.startDate = startDate
    if (endDate) request.endDate = endDate

    request.page = asPositiveInteger(pagination.page ?? filters.page, DEFAULT_PAGE)
    request.limit = asPositiveInteger(pagination.limit ?? filters.limit, DEFAULT_LIMIT)

    return request
}

export const normalizeEventReportRow = row => {
    const incidents = Array.isArray(row?.incidents) ? row.incidents : Array.isArray(row?.incident) ? row.incident : []
    const media = row?.media || row?.medias || {}
    const attendance = row?.attendance || {}

    return {
        id: row?._id || row?.id || '',
        date: row?.date || row?.createdAt || '',
        type: row?.type || '',
        source: normalizedSource(row?.source),
        status: row?.status || '',
        account: displayName(row?.account, row?.accountName || row?.companyName),
        client: displayName(row?.client, row?.clientName),
        site: displayName(row?.site, row?.siteName || row?.localName),
        vigilant: displayName(row?.vigilant),
        event: displayName(row?.event),
        geolocation: row?.geolocation || {},
        attendance,
        attended: typeof row?.attended === 'boolean' ? row.attended : attendance.isAttendance === true,
        treatments: Array.isArray(row?.treatments) ? row.treatments : [],
        incidents,
        incidentsText: incidents.map(displayName).filter(Boolean).join(', '),
        notes: row?.notes || '',
        photoURL: row?.photoURL || media?.photoURL || '',
        soundURL: row?.soundURL || media?.soundURL || '',
        signatureURL: row?.signatureURL || media?.signatureURL || '',
        deviceInfo: row?.deviceInfo || {},
    }
}

export const normalizeEventReportResponse = payload => {
    const body = payload?.data || payload || {}
    const rawRows = Array.isArray(body.results) ? body.results : []
    const shieldGoRows = rawRows.filter(row => normalizedSource(row?.source) === 'SHIELDGO')
    const excludedCount = rawRows.length - shieldGoRows.length
    const providedTotal = Number.isFinite(Number(body.totalCount)) ? Number(body.totalCount) : rawRows.length
    const totalCount = Math.max(shieldGoRows.length, providedTotal - excludedCount)

    return {
        results: shieldGoRows.map(normalizeEventReportRow),
        totalCount,
        backendTotalCount: providedTotal,
        page: asPositiveInteger(body.page, DEFAULT_PAGE),
        limit: asPositiveInteger(body.limit, DEFAULT_LIMIT),
        receivedCount: rawRows.length,
    }
}

export const toEventReportExportRow = (row, labels = {}) => {
    const attendance = row?.attendance || {}
    const operator = displayName(attendance.operator)
    const attended = typeof row?.attended === 'boolean' ? row.attended : attendance.isAttendance === true
    const attendanceLabel = attended
        ? labels.attended || labels.yes || 'Attended'
        : labels.notAttended || labels.no || 'Not attended'
    const formatDate = typeof labels.formatDate === 'function' ? labels.formatDate : stringValue
    const translate = typeof labels.translate === 'function' ? labels.translate : stringValue
    const treatments = (Array.isArray(row?.treatments) ? row.treatments : [])
        .map(treatment => [
            translate(treatment.type),
            treatment.notes,
            displayName(treatment.operator),
            formatDate(treatment.createDate),
        ].filter(Boolean).join(' — '))
        .filter(Boolean)
        .join('\n')

    return {
        date: formatDate(row?.date),
        type: translate(row?.type || ''),
        source: normalizedSource(row?.source),
        status: row?.status || '',
        account: row?.account || '',
        client: row?.client || '',
        site: row?.site || '',
        vigilant: row?.vigilant || '',
        event: row?.event || '',
        latitude: stringValue(row?.geolocation?.latitude),
        longitude: stringValue(row?.geolocation?.longitude),
        attendance: attendanceLabel,
        attendanceStatus: attendance.status || '',
        operator,
        openedDate: formatDate(attendance.openedDate),
        closedDate: formatDate(attendance.closedDate),
        treatments,
        incidents: row?.incidentsText || '',
        notes: row?.notes || '',
        deviceBrand: row?.deviceInfo?.brand || '',
        deviceModel: row?.deviceInfo?.model || '',
        photoURL: row?.photoURL || '',
        soundURL: row?.soundURL || '',
        signatureURL: row?.signatureURL || '',
    }
}
